import React from "react";
import {
  DEFAULT_HEARTBEAT_MINUTES,
  heartbeatMinutes,
} from "../../utils/heartbeat";
import {
  REAR_TRAY_MEDIA,
  REAR_TRAY_MAX,
  photosOff,
} from "../../utils/rearTray";
import {
  ADDRESS_MAX,
  HOURS_MAX,
  placeOf,
  pinText,
  pinOf,
  mapUrl,
} from "../../utils/place";
import { Toggle } from "../settings/AlertCard";
import { fieldClass } from "../template/templateLabels";
import { MarkPicker } from "../service/VisitForm";

// The edit form of a printer: its fields in three tabs, what they start from, and
// what of them is saved. The fields start from what the printer has, and the form
// keeps only what was typed over that, so the fields nobody touched follow the
// printer while the Printers page refreshes it under the open form.

// The ink tanks a worker records, and the marks of each: where they found it and
// where they filled it to. The printer keeps neither, so both start empty.
export const TANKS = ["black", "color"];
const markKeys = (tank) => [tank + "Before", tank + "After"];
export const INK_FIELDS = TANKS.flatMap(markKeys);

export const TABS = [
  {
    key: "paper",
    label: "Paper & ink",
    icon: "fa-layer-group",
    fields: [
      "paper",
      ...INK_FIELDS,
      "photoPrinting",
      "rearTrayMedia",
      "rearTrayPaper",
    ],
  },
  {
    key: "location",
    label: "Location",
    icon: "fa-map-marker-alt",
    fields: ["location", "address", "hours", "pin", "listed"],
  },
  {
    key: "general",
    label: "General",
    icon: "fa-sliders-h",
    fields: ["printerName", "phoneNumber", "heartbeatMinutes"],
  },
];

// Minutes, as the backend checks them in seconds
const HEARTBEAT_MIN = 1;
const HEARTBEAT_MAX = 60;
// More is a typing mistake, as the backend's utils/service.js has it
const PAPER_MAX = 1000;

// The fields as the printer has them: texts as the inputs hold them, the two
// switches, and no ink marks
export const savedOf = (printer) => {
  const place = placeOf(printer);
  const tray = printer.rearTray || {};
  return {
    paper: String(printer.paper || 0),
    blackBefore: null,
    blackAfter: null,
    colorBefore: null,
    colorAfter: null,
    photoPrinting: !photosOff(printer),
    rearTrayMedia: REAR_TRAY_MEDIA.some((item) => item.value === tray.media)
      ? tray.media
      : "none",
    rearTrayPaper: String(typeof tray.paper === "number" ? tray.paper : 0),
    location: printer.location || "",
    address: place.address || "",
    hours: place.hours || "",
    pin: pinText(printer),
    listed: place.listed === true,
    printerName: printer.printerName || "",
    phoneNumber: printer.phoneNumber || "",
    heartbeatMinutes: String(heartbeatMinutes(printer)),
  };
};

// The fields that no longer hold what the printer has
export const changedIn = (form, saved) =>
  Object.keys(saved).filter((key) => form[key] !== saved[key]);

const isCount = (text, max = Infinity) =>
  /^\d+$/.test(text.trim()) && Number(text) <= max;

// What keeps the form from being saved, by field. Only the changed fields are
// checked: a paper count the kiosk has taken below zero must not keep its address
// from being saved. The backend refuses these too.
export const problemsOf = (form, changed) => {
  const problems = {};
  const check = (key, ok, text) => {
    if (changed.includes(key) && !ok) problems[key] = text;
  };
  const minutes = Number(form.heartbeatMinutes);
  check("paper", isCount(form.paper, PAPER_MAX), `0 to ${PAPER_MAX} sheets`);
  TANKS.forEach((tank) => {
    const [before, after] = markKeys(tank).map((key) => form[key]);
    check(
      tank + "After",
      before === null || after === null || after >= before,
      "Filled to a mark below where it was found"
    );
  });
  check(
    "rearTrayPaper",
    isCount(form.rearTrayPaper, REAR_TRAY_MAX),
    `0 to ${REAR_TRAY_MAX} sheets`
  );
  check(
    "pin",
    Boolean(pinOf(form.pin)),
    "Two numbers, the latitude and then the longitude"
  );
  check("printerName", form.printerName.trim() !== "", "A kiosk needs a name");
  check(
    "heartbeatMinutes",
    form.heartbeatMinutes.trim() === "" ||
      (minutes >= HEARTBEAT_MIN && minutes <= HEARTBEAT_MAX),
    `${HEARTBEAT_MIN} to ${HEARTBEAT_MAX} minutes`
  );
  // The kiosks list a kiosk under its location
  if (
    (changed.includes("listed") || changed.includes("location")) &&
    form.listed &&
    !form.location.trim()
  ) {
    problems.location = "A listed kiosk needs a location";
  }
  return problems;
};

// What editPrinter is given: the changed fields alone. A rear tray count is dated
// when it is typed, with the +8 hours of the printer's other dates. The A4 count and
// the ink marks are a service visit, which the backend dates.
export const changesOf = (form, changed) => {
  const now = new Date();
  now.setHours(now.getHours() + 8);
  const fields = {};
  // A tank's two marks go together, null for one not picked
  const tank = (name) => () => {
    const [before, after] = markKeys(name).map((key) => form[key]);
    fields[name] = { before, after };
  };
  const read = {
    paper: () => (fields.paper = form.paper.trim()),
    blackBefore: tank("black"),
    blackAfter: tank("black"),
    colorBefore: tank("color"),
    colorAfter: tank("color"),
    photoPrinting: () => (fields.photoEnabled = form.photoPrinting),
    rearTrayMedia: () => (fields.rearTrayMedia = form.rearTrayMedia),
    rearTrayPaper: () => {
      fields.rearTrayPaper = form.rearTrayPaper.trim();
      fields.rearTrayPaperDate = now;
    },
    // null empties what "" would leave unchanged
    location: () => (fields.location = form.location.trim() || null),
    address: () => (fields.address = form.address),
    hours: () => (fields.hours = form.hours),
    pin: () => Object.assign(fields, pinOf(form.pin)),
    listed: () => (fields.listed = form.listed),
    printerName: () => (fields.printerName = form.printerName.trim()),
    phoneNumber: () => (fields.phoneNumber = form.phoneNumber.trim() || null),
    // Emptied, it goes back to the default
    heartbeatMinutes: () =>
      (fields.heartbeatMinutes = form.heartbeatMinutes.trim() || null),
  };
  changed.forEach((key) => read[key]());
  return fields;
};

// The tabs of the form, or of the printer's details, which have one more. A dot marks
// a tab with changes, in red when one of them keeps the form from being saved. A
// phone has no room for the icons.
export const PrinterTabs = ({ tabs, tab, onTab, changed, problems }) => (
  <div
    role="tablist"
    className="flex flex-shrink-0 px-6 space-x-5 border-b border-gray-100 sm:space-x-6 dark:border-gray-700"
  >
    {tabs.map(({ key, label, icon, fields }) => {
      const bad = fields.some((field) => problems[field]);
      const touched = fields.some((field) => changed.includes(field));
      return (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={tab === key}
          onClick={() => onTab(key)}
          className={
            "flex items-center py-3 -mb-px text-sm font-medium border-b-2 focus:outline-none " +
            (tab === key
              ? "text-purple-600 border-purple-600 dark:text-purple-300 dark:border-purple-400"
              : "text-gray-500 border-transparent hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200")
          }
        >
          <span className="hidden mr-2 sm:inline" aria-hidden="true">
            <i className={"fas " + icon} />
          </span>
          {label}
          {(bad || touched) && (
            <span
              className={
                "w-2 h-2 ml-2 rounded-full " +
                (bad ? "bg-red-500" : "bg-purple-500")
              }
              title={bad ? "Needs fixing" : "Changed"}
            />
          )}
        </button>
      );
    })}
  </div>
);

const idOf = (key) => "printer-" + key;

// The field of one key under its label, with its help or, in red, what is wrong
// with it
const Field = ({ name, label, help, problem, children }) => (
  <div>
    <label
      htmlFor={idOf(name)}
      className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      {label}
    </label>
    {children}
    {(problem || help) && (
      <p
        className={
          "mt-1 text-xs " +
          (problem
            ? "text-red-600 dark:text-red-400"
            : "text-gray-500 dark:text-gray-400")
        }
      >
        {problem || help}
      </p>
    )}
  </div>
);

const SwitchRow = ({ label, text, checked, onChange }) => (
  <div className="flex items-start justify-between">
    <div className="min-w-0 mr-4">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{text}</p>
    </div>
    <Toggle checked={checked} onChange={onChange} label={label} />
  </div>
);

// One ink tank: the mark it was found at and the one it was filled to, either left out
// when nobody looked or filled, over what the backend estimates it holds now
const TankField = ({ name, label, note, estimate, marks, form, problem, onChange }) => {
  const [before, after] = markKeys(name);
  const row = (key, text) => (
    <div className="sm:flex sm:items-center">
      <p className="mb-1 text-xs text-gray-600 sm:mb-0 sm:w-24 sm:flex-shrink-0 dark:text-gray-400">
        {text}
      </p>
      <MarkPicker
        marks={marks}
        value={form[key]}
        onChange={(mark) => onChange(key, mark)}
      />
    </div>
  );
  return (
    <div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </p>
      <p className="mt-1 mb-3 text-xs text-gray-500 dark:text-gray-400">
        {note}
        {estimate
          ? `Now about ${estimate.level} of ${estimate.marks} marks.`
          : "Not recorded at this kiosk yet."}
      </p>
      <div className="space-y-3">
        {row(before, "Found at")}
        {row(after, "Filled to")}
      </div>
      {problem && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{problem}</p>
      )}
    </div>
  );
};

// The fields of the open tab. ink: the kiosk's tanks as the backend estimates them,
// and marks: how many lines a tank has above its lowest.
export const PrinterFields = ({ tab, form, problems, onChange, ink, marks }) => {
  const badClass = (key) =>
    problems[key] ? " border-red-400 dark:border-red-500" : "";
  const input = (key, props) => (
    <input
      type="text"
      id={idOf(key)}
      aria-invalid={Boolean(problems[key])}
      value={form[key]}
      onChange={(e) => onChange(key, e.target.value)}
      className={fieldClass + badClass(key)}
      {...props}
    />
  );
  // A number with its unit. A press on it selects the number, to type over.
  const count = (key, unit) => (
    <span className="relative block">
      {input(key, {
        inputMode: "decimal",
        onFocus: (e) => e.target.select(),
        className: fieldClass + " pr-20 tabular-nums" + badClass(key),
      })}
      <span className="absolute inset-y-0 flex items-center text-sm text-gray-400 pointer-events-none right-3">
        {unit}
      </span>
    </span>
  );

  if (tab === "paper") {
    return (
      <>
        <Field
          name="paper"
          label="A4 paper"
          problem={problems.paper}
          help="The sheets in the kiosk now, which it counts down as it prints."
        >
          {count("paper", "sheets")}
        </Field>
        <div className="pt-5 space-y-5 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Count a tank's marks from its lowest line (0) to full ({marks}).
            Pick where you found it, and where you filled it to if you did: the
            ink left is worked out from them and the pages printed since.
          </p>
          <TankField
            name="black"
            label="Black ink"
            estimate={ink && ink.black}
            marks={marks}
            form={form}
            problem={problems.blackAfter}
            onChange={onChange}
          />
          <TankField
            name="color"
            label="Color ink"
            note="The lowest of the three color tanks. "
            estimate={ink && ink.color}
            marks={marks}
            form={form}
            problem={problems.colorAfter}
            onChange={onChange}
          />
        </div>
        <div className="pt-5 space-y-5 border-t border-gray-100 dark:border-gray-700">
          <SwitchRow
            label="Photo printing"
            text="Off until switched on. An off kiosk hides its photo card and is given no photo or ID photo to print, whatever its rear tray holds."
            checked={form.photoPrinting}
            onChange={(value) => onChange("photoPrinting", value)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field name="rearTrayMedia" label="Rear tray paper">
              <select
                id={idOf("rearTrayMedia")}
                value={form.rearTrayMedia}
                onChange={(e) => onChange("rearTrayMedia", e.target.value)}
                className={fieldClass + " pr-8"}
              >
                {REAR_TRAY_MEDIA.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              name="rearTrayPaper"
              label="Rear tray sheets"
              problem={problems.rearTrayPaper}
            >
              {count("rearTrayPaper", "sheets")}
            </Field>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The kiosk sells photos only while photo printing is on, the tray
            holds photo paper and its printer has the CloudPrint Photo queue.
          </p>
        </div>
      </>
    );
  }

  if (tab === "location") {
    const pin = pinOf(form.pin);
    return (
      <>
        <Field
          name="location"
          label="Location"
          problem={problems.location}
          help="What the kiosk goes by across the admin, in the mails and in the kiosks' Locations window."
        >
          {input("location", { placeholder: "МУИС номын сан" })}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field name="address" label="Address">
            {input("address", {
              maxLength: ADDRESS_MAX,
              placeholder: "1 давхар",
            })}
          </Field>
          <Field name="hours" label="Opening hours">
            {input("hours", {
              maxLength: HOURS_MAX,
              placeholder: "Даваа - Баасан 07:00 - 22:00",
            })}
          </Field>
        </div>
        <Field
          name="pin"
          label="Coordinates"
          problem={problems.pin}
          help={
            pin && pin.lat !== null ? (
              <a
                href={mapUrl(pin)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:underline dark:text-purple-300"
              >
                <i
                  className="mr-1 fas fa-external-link-alt"
                  aria-hidden="true"
                />
                Check on the map
              </a>
            ) : (
              "Right-click the place on Google Maps to copy them. Without them the kiosks show no map QR."
            )
          }
        >
          {input("pin", { placeholder: "47.9226866, 106.9205694" })}
        </Field>
        <div className="pt-5 border-t border-gray-100 dark:border-gray-700">
          <SwitchRow
            label="Show in the kiosks' Locations window"
            text="Every kiosk lists the kiosks switched on here, by name, so a customer at one can find another. A change reaches them within minutes."
            checked={form.listed}
            onChange={(value) => onChange("listed", value)}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Field name="printerName" label="Name" problem={problems.printerName}>
        {input("printerName")}
      </Field>
      <Field
        name="phoneNumber"
        label="Phone number"
        help="The kiosk shows it to customers who need help. Several numbers can be separated by spaces."
      >
        {input("phoneNumber")}
      </Field>
      <Field
        name="heartbeatMinutes"
        label="Heartbeat interval"
        problem={problems.heartbeatMinutes}
        help={`How often the kiosk reports that it is alive. Left empty, it goes back to ${DEFAULT_HEARTBEAT_MINUTES} minutes.`}
      >
        {count("heartbeatMinutes", "minutes")}
      </Field>
    </>
  );
};
